import { cn } from "@/lib/utils";
import { useMsAdlCopy } from "@/features/i18n/msAdlCopy";
import { BaseEntryValue } from "./BaseEntryValue";
import type { BaseEntryValueProps } from "./BaseEntryValue";

export interface MultipleChoiceEntryValueProps extends Omit<BaseEntryValueProps, "value"> {
    value: string[] | string | null;
    /** When true, map known MS-ADL activity keys to the active locale. */
    localiseMsAdlActivities?: boolean;
}

export function MultipleChoiceEntryValue({
    value,
    inputLabel,
    size,
    className,
    localiseMsAdlActivities = false,
    ...props
}: MultipleChoiceEntryValueProps) {
    const { formatActivity } = useMsAdlCopy();

    // Parse value - handles arrays and PostgreSQL array strings
    const parseValue = (val: string[] | string | null): string[] => {
        if (!val) return [];

        // Already an array
        if (Array.isArray(val)) {
            // Check if array items themselves are PostgreSQL format strings
            return val.flatMap((item) => {
                if (typeof item === "string" && item.startsWith("{") && item.endsWith("}")) {
                    return item
                        .slice(1, -1)
                        .split(",")
                        .map((s) => s.trim().replace(/^"|"$/g, ""))
                        .filter(Boolean);
                }
                return item;
            });
        }

        // Handle PostgreSQL format like "{item1,item2}" or "{item1}"
        if (typeof val === "string") {
            // Remove outer braces if present
            const cleaned = val.replace(/^\{|\}$/g, "");
            if (!cleaned) return [];

            return cleaned
                .split(",")
                .map((item) => item.trim().replace(/^"|"$/g, "")) // Remove quotes
                .filter(Boolean);
        }

        return [];
    };

    const parsedValue = parseValue(value);
    const displayValue = localiseMsAdlActivities ? parsedValue.map(formatActivity) : parsedValue;

    return (
        <BaseEntryValue inputLabel={inputLabel} size={size} className={className} {...props}>
            <p
                className={cn(
                    "text-slate-900 dark:text-slate-200",
                    size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-base"
                )}
            >
                {displayValue.length > 0 ? displayValue.join(", ") : "None"}
            </p>
        </BaseEntryValue>
    );
}
